import { customizationDemandFieldLabels } from "../config";
import type {
  CustomizationDemandDraft,
  CustomizationDemandVersionOption,
  CustomizationProjectListItem,
  CustomizationProjectSnapshot,
} from "../types";

export type DemandExcelDetailRow = {
  key?: string;
  label: string;
  value: string;
  changed: boolean;
};

type BuildDemandExcelDetailRowsInput = {
  demand: CustomizationDemandDraft;
  version: Pick<
    CustomizationDemandVersionOption,
    "orderVersionKey" | "versionNumber" | "versionSeq" | "createdAt" | "updatedAt"
  >;
  projectContext: Pick<
    CustomizationProjectListItem,
    | "name"
    | "projectCode"
    | "legacyId"
    | "customerType"
    | "tripType"
    | "signType"
    | "dealProgress"
    | "salesUserName"
    | "dispatcherUserName"
    | "operatingCompany"
    | "organizationNameSnapshot"
    | "customerName"
    | "contactName"
    | "projectCreatedAt"
  >;
  project?: CustomizationProjectSnapshot | null;
};

/**
 * 列表「查看版本-详细」只读表：展示工作台需求定制的全部表单参数。
 * 顶部保留版本身份信息；表单字段标签与工作台 `customizationDemandFieldLabels` 一致。
 */
export function buildDemandExcelDetailRows(
  input: BuildDemandExcelDetailRowsInput,
): DemandExcelDetailRow[] {
  const { demand, version, projectContext } = input;
  const project = resolveProjectSnapshot(input);
  const changedKeys = new Set(Object.keys(demand.changedFields ?? {}));
  const batchNo =
    project.legacyId?.trim() ||
    project.projectCode?.trim() ||
    projectContext.legacyId?.trim() ||
    projectContext.projectCode?.trim() ||
    "--";
  const orderVersionKey =
    version.orderVersionKey?.trim() ||
    `${batchNo}_${version.versionSeq || demand.versionSeq || 1}`;
  const opportunityName =
    project.name?.trim() || projectContext.name?.trim() || demand.demandSummary?.trim() || "--";

  const summary = demand.summary;
  const customer = demand.customerRequirements;
  const teacher = demand.teacherRequirements;
  const service = demand.serviceStandard;
  const relations = demand.relations;

  const identityRows: DemandExcelDetailRow[] = [
    row("identity.projectCode", "定制号", batchNo),
    row("identity.orderVersionKey", "定制版本号", orderVersionKey),
    row("identity.projectCreatedAt", "定制发起时间", projectContext.projectCreatedAt),
    row(
      "identity.updatedAt",
      "当前版本修改时间",
      demand.updatedAt || version.updatedAt || version.createdAt,
    ),
    row("identity.opportunityName", "机会名称", opportunityName),
    row("identity.salesUserName", "销售姓名", project.salesUserName ?? projectContext.salesUserName),
  ];

  const formRows: DemandExcelDetailRow[] = [
    field(
      "primarySchoolNameSnapshot",
      project.primarySchoolNameSnapshot,
      changedKeys,
    ),
    field("customerName", project.customerName, changedKeys),
    field("contactName", project.contactName, changedKeys),
    field("majorNames", relations?.majorNames, changedKeys),
    field(
      "departureStartDate",
      summary?.departureStartDate ?? demand.departureStartDate,
      changedKeys,
    ),
    field("durationDays", summary?.durationDays ?? demand.durationDays, changedKeys),
    field("studentCount", summary?.studentCount ?? demand.studentCount, changedKeys),
    field("teacherCount", summary?.teacherCount ?? demand.teacherCount, changedKeys),
    field(
      "destinationSummary",
      summary?.destinationSummary ?? demand.destinationSummary,
      changedKeys,
    ),
    field("provinces", relations?.provinces, changedKeys),
    field("stayAreaNames", relations?.stayAreaNames, changedKeys),
    field("pointNames", relations?.pointNames, changedKeys),
    field("budgetPerPerson", summary?.budgetPerPerson ?? demand.budgetPerPerson, changedKeys),
    field("customerType", customer?.customerType ?? project.customerType, changedKeys),
    field("tripType", customer?.tripType ?? project.tripType, changedKeys),
    field("signType", customer?.signType ?? project.signType, changedKeys),
    field(
      "schoolPickupRequirement",
      customer?.schoolPickupRequirement ?? demand.schoolPickupRequirement,
      changedKeys,
    ),
    field("dispatcherUserName", project.dispatcherUserName, changedKeys),
    field(
      "operatingCompany",
      customer?.operatingCompany ?? project.operatingCompany,
      changedKeys,
    ),
    field(
      "teachingRequirements",
      customer?.teachingRequirements ?? demand.teachingRequirements,
      changedKeys,
    ),
    field(
      "specialRequirements",
      customer?.specialRequirements ?? demand.specialRequirements,
      changedKeys,
    ),
    field(
      "invoiceRequirement",
      customer?.invoiceRequirement ?? demand.invoiceRequirement,
      changedKeys,
    ),
    field("remark", customer?.remark ?? demand.remark, changedKeys),
    field(
      "teacherMealRequirement",
      teacher?.teacherMealRequirement ?? demand.teacherMealRequirement,
      changedKeys,
    ),
    field(
      "teacherTicketRequirement",
      teacher?.teacherTicketRequirement ?? demand.teacherTicketRequirement,
      changedKeys,
    ),
    field(
      "teacherTransportRequirement",
      teacher?.teacherTransportRequirement ?? demand.teacherTransportRequirement,
      changedKeys,
    ),
    field(
      "teacherAccommodationRequirement",
      teacher?.teacherAccommodationRequirement ?? demand.teacherAccommodationRequirement,
      changedKeys,
    ),
    field("guideCount", service?.guideCount, changedKeys),
    field("guideNote", service?.guideNote, changedKeys),
    field(
      "includeTickets",
      formatIncludeFlag(service?.includeTickets, "包含考察点门票", "不包含考察点门票"),
      changedKeys,
    ),
    field(
      "includeMeals",
      formatIncludeFlag(service?.includeMeals, "全程含餐", "全程不含餐"),
      changedKeys,
    ),
    field("roundTripTransportNote", service?.roundTripTransportNote, changedKeys),
    field("localTransportNote", service?.localTransportNote, changedKeys),
    field("accommodationNote", service?.accommodationNote, changedKeys),
    field("companyVisitCount", service?.companyVisitCount, changedKeys),
    field("intangibleHeritageCount", service?.intangibleHeritageCount, changedKeys),
    field("ticketNote", service?.ticketNote, changedKeys),
    field("mealStandards", service?.mealStandards, changedKeys),
    field("mealNote", service?.mealNote, changedKeys),
    field("companyVisitNote", service?.companyVisitNote, changedKeys),
    field("intangibleHeritageNote", service?.intangibleHeritageNote, changedKeys),
  ];

  return [...identityRows, ...formRows];
}

/** 生成 Excel 详情标题，对齐旧系统“【花儿定制{JiDiaoNo}】机会名”。 */
export function buildDemandExcelDetailTitle(input: {
  orderVersionKey?: string;
  projectCode?: string;
  legacyId?: string;
  versionSeq?: number;
  opportunityName?: string;
}): string {
  const batchNo = input.legacyId?.trim() || input.projectCode?.trim() || "--";
  const orderVersionKey =
    input.orderVersionKey?.trim() || `${batchNo}_${input.versionSeq || 1}`;
  const opportunityName = input.opportunityName?.trim() || "--";
  return `【花儿定制${orderVersionKey}】${opportunityName}`;
}

/** 用列表上下文拼出工作台字段所需的项目快照。 */
function resolveProjectSnapshot(
  input: BuildDemandExcelDetailRowsInput,
): CustomizationProjectSnapshot {
  const { project, projectContext, demand } = input;
  return {
    id: project?.id || demand.id || "project-context",
    legacyId: project?.legacyId ?? projectContext.legacyId,
    projectCode: project?.projectCode || projectContext.projectCode || "",
    name: project?.name || projectContext.name || "",
    customerName: project?.customerName ?? projectContext.customerName,
    contactName: project?.contactName ?? projectContext.contactName,
    customerType: project?.customerType ?? projectContext.customerType,
    tripType: project?.tripType ?? projectContext.tripType,
    signType: project?.signType ?? projectContext.signType,
    dealProgress: project?.dealProgress ?? projectContext.dealProgress,
    salesUserName: project?.salesUserName ?? projectContext.salesUserName,
    dispatcherUserName: project?.dispatcherUserName ?? projectContext.dispatcherUserName,
    operatingCompany: project?.operatingCompany ?? projectContext.operatingCompany,
    primarySchoolNameSnapshot:
      project?.primarySchoolNameSnapshot ?? projectContext.organizationNameSnapshot,
    primarySchoolOrgType: project?.primarySchoolOrgType,
  };
}

/** 构造一行工作台字段。 */
function field(
  key: string,
  value: unknown,
  changedKeys: Set<string>,
): DemandExcelDetailRow {
  return {
    key: `form.${key}`,
    label: customizationDemandFieldLabels[key] ?? key,
    value: displayText(value),
    changed: changedKeys.has(key),
  };
}

/** 构造一行固定身份字段。 */
function row(key: string, label: string, value: unknown): DemandExcelDetailRow {
  return {
    key,
    label,
    value: displayText(value),
    changed: false,
  };
}

/** 将布尔服务标志格式化为中文包含文案。 */
function formatIncludeFlag(
  value: boolean | undefined,
  yesText: string,
  noText: string,
): string {
  if (value === true) return yesText;
  if (value === false) return noText;
  return "";
}

/** 将未知值规整为可展示文本。 */
function displayText(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join("、");
  }
  if (typeof value === "number") return String(value);
  return String(value).trim();
}
